import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { useTimezone } from '@/hooks/useTimezone';
import { formatDateTime } from '@/lib/datetime';
import type { ConselhoRun } from '@/services/types';

/**
 * A code as words when the locale names it, else the code with its
 * underscores opened up -- never a bare key, never an empty label.
 */
function named(t: TFunction, key: string, code: string): string {
  return t(key, { defaultValue: code.replace(/_/g, ' ') });
}

/**
 * `bootstrap_v1` -> method `bootstrap`, version `1`. A version string without
 * the `_vN` suffix is shown whole as the method.
 */
function splitVersion(modelVersion: string): { method: string; version: string | null } {
  const m = /^(.*)_v(\d+)$/.exec(modelVersion);
  return m ? { method: m[1], version: m[2] } : { method: modelVersion, version: null };
}

/**
 * The model run a panel was read from, with its sampler diagnostics.
 *
 * Every figure on the Conselho Diretor screens is READ from a registered run,
 * never computed here, so the run id is the provenance a reader (and an
 * export) can cite. A run that failed the acceptance rule writes no figures,
 * and that is said in so many words rather than left as an empty panel.
 *
 * The id (`40:severity_A:bootstrap_v1:202609281852`) is the ETL's key, not
 * something a lawyer reads: the footer names the model, the method and the
 * date the run was built, and keeps the id in `data-run-id` and the tooltip
 * for anyone who needs to cite it.
 */
export function RunFooter({ run, ns = 'anatel' }: { run: ConselhoRun | null; ns?: string }) {
  const { t, i18n } = useTranslation([ns, 'common']);
  const { timezone } = useTimezone();
  if (!run) return null;
  const number = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 3 });
  const { method, version } = splitVersion(run.model_version);
  const built = Number.isNaN(Date.parse(run.built_at)) ? run.built_at : formatDateTime(run.built_at, timezone);
  const described = t('common:conselhoRun.describe', {
    model: named(t, `common:conselhoRun.models.${run.model}`, run.model),
    method: named(t, `common:conselhoRun.methods.${method}`, method),
    version: version ?? '',
    date: built,
  });
  return (
    <p className="mt-2 text-[11px] text-muted-foreground" data-run-id={run.run_id} title={run.run_id}>
      {t(`${ns}:conselho.run.label`)}: {described} —{' '}
      {run.accepted
        ? t(`${ns}:conselho.run.diagnostics`, {
            n: run.n_obs,
            rhat: number.format(run.max_rhat ?? 0),
            ess: Math.round(run.min_ess ?? 0),
            divergences: run.divergences ?? 0,
          })
        : t(`${ns}:conselho.run.notAccepted`)}
    </p>
  );
}
